import { Routes, Route, Navigate } from "react-router-dom";
import SystemStatusPage from "@/pages/system/SystemStatusPage";
import LoginPage from "@/pages/auth/LoginPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import PortalProtectedRoute from "@/components/common/PortalProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import PortalLayout from "@/components/layout/PortalLayout";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import ClientsPage from "@/pages/clients/ClientsPage";
import ClientDetailPage from "@/pages/clients/ClientDetailPage";
import OperacionPage from "@/pages/operacion/OperacionPage";
import TasksPage from "@/pages/tasks/TasksPage";
import ServicesPage from "@/pages/services/ServicesPage";
import ProceduresPage from "@/pages/procedures/ProceduresPage";
import TaxesPage from "@/pages/taxes/TaxesPage";
import LegalCasesPage from "@/pages/legal/LegalCasesPage";
import ChartOfAccountsPage from "@/pages/accounting/ChartOfAccountsPage";
import JournalEntriesPage from "@/pages/accounting/JournalEntriesPage";
import EmployeesPage from "@/pages/employees/EmployeesPage";
import PayrollRunsPage from "@/pages/payroll/PayrollRunsPage";
import InvoicesPage from "@/pages/invoices/InvoicesPage";
import PaymentsPage from "@/pages/payments/PaymentsPage";
import CarteraPage from "@/pages/cartera/CarteraPage";
import ReportsPage from "@/pages/reports/ReportsPage";
import UsersPage from "@/pages/users/UsersPage";
import RolesPage from "@/pages/roles/RolesPage";
import AuditPage from "@/pages/audit/AuditPage";
import AutomationRulesPage from "@/pages/automations/AutomationRulesPage";
import OcrReviewPage from "@/pages/ocr/OcrReviewPage";
import WorkbenchesPage from "@/pages/workbenches/WorkbenchesPage";
import WorkbenchDetailPage from "@/pages/workbenches/WorkbenchDetailPage";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import DocumentGeneratorPage from "@/pages/documents/DocumentGeneratorPage";
import OrganizationsPage from "@/pages/admin/OrganizationsPage";
import ProductsPage from "@/pages/store/ProductsPage";
import PurchasesPage from "@/pages/store/PurchasesPage";
import SuppliersPage from "@/pages/store/SuppliersPage";
import SupplierPayablesPage from "@/pages/store/SupplierPayablesPage";
import QuotesPage from "@/pages/store/QuotesPage";
import PurchaseQuotesPage from "@/pages/store/PurchaseQuotesPage";
import LayawaysPage from "@/pages/store/LayawaysPage";
import PosPage from "@/pages/store/PosPage";
import StoreReportsPage from "@/pages/store/StoreReportsPage";
import StockCountsPage from "@/pages/store/StockCountsPage";
import PromotionsPage from "@/pages/store/PromotionsPage";
import WarrantiesPage from "@/pages/store/WarrantiesPage";
import BranchesPage from "@/pages/store/BranchesPage";
import StockTransfersPage from "@/pages/store/StockTransfersPage";
import KardexPage from "@/pages/store/KardexPage";
import CreditsPage from "@/pages/store/CreditsPage";
import StoreCustomersPage from "@/pages/store/StoreCustomersPage";
import PriceCheckPage from "@/pages/store/PriceCheckPage";
import SalesHistoryPage from "@/pages/store/SalesHistoryPage";
import ReturnsPage from "@/pages/store/ReturnsPage";
import CashShiftsPage from "@/pages/store/CashShiftsPage";
import ShrinkagePage from "@/pages/store/ShrinkagePage";
import AlertsPage from "@/pages/store/AlertsPage";
import PortalDashboardPage from "@/pages/portal/PortalDashboardPage";
import PortalDocumentsPage from "@/pages/portal/PortalDocumentsPage";
import PortalProceduresPage from "@/pages/portal/PortalProceduresPage";
import PortalLegalCasesPage from "@/pages/portal/PortalLegalCasesPage";
import PortalInvoicesPage from "@/pages/portal/PortalInvoicesPage";
import SecuritySettingsPage from "@/pages/settings/SecuritySettingsPage";
import { useSessionBootstrap } from "@/hooks/useSessionBootstrap";

/**
 * Enrutador raíz. Cada fase agrega sus propias rutas protegidas dentro de
 * <AppLayout /> (sidebar + contenido). Los módulos de negocio (Clientes,
 * Trámites, Impuestos...) se agregan aquí a medida que se construyen.
 */
export default function App() {
  const { isBootstrapping } = useSessionBootstrap();

  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-400">Cargando...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/status" element={<SystemStatusPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/operacion" element={<OperacionPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/procedures" element={<ProceduresPage />} />
          <Route path="/taxes" element={<TaxesPage />} />
          <Route path="/legal-cases" element={<LegalCasesPage />} />
          <Route path="/accounting/accounts" element={<ChartOfAccountsPage />} />
          <Route path="/accounting/entries" element={<JournalEntriesPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/payroll/runs" element={<PayrollRunsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/cartera" element={<CarteraPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/automations" element={<AutomationRulesPage />} />
          <Route path="/ocr-review" element={<OcrReviewPage />} />
          <Route path="/workbenches" element={<WorkbenchesPage />} />
          <Route path="/workbenches/:id" element={<WorkbenchDetailPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/document-generator" element={<DocumentGeneratorPage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/store/products" element={<ProductsPage />} />
          <Route path="/store/purchases" element={<PurchasesPage />} />
          <Route path="/store/suppliers" element={<SuppliersPage />} />
          <Route path="/store/payables" element={<SupplierPayablesPage />} />
          <Route path="/store/quotes" element={<QuotesPage />} />
          <Route path="/store/purchase-quotes" element={<PurchaseQuotesPage />} />
          <Route path="/store/layaways" element={<LayawaysPage />} />
          <Route path="/store/pos" element={<PosPage />} />
          <Route path="/store/reports" element={<StoreReportsPage />} />
          <Route path="/store/stock-counts" element={<StockCountsPage />} />
          <Route path="/store/promotions" element={<PromotionsPage />} />
          <Route path="/store/warranties" element={<WarrantiesPage />} />
          <Route path="/store/branches" element={<BranchesPage />} />
          <Route path="/store/transfers" element={<StockTransfersPage />} />
          <Route path="/store/kardex" element={<KardexPage />} />
          <Route path="/store/credits" element={<CreditsPage />} />
          <Route path="/store/customers" element={<StoreCustomersPage />} />
          <Route path="/store/price-check" element={<PriceCheckPage />} />
          <Route path="/store/sales-history" element={<SalesHistoryPage />} />
          <Route path="/store/returns" element={<ReturnsPage />} />
          <Route path="/store/cash-shifts" element={<CashShiftsPage />} />
          <Route path="/store/shrinkage" element={<ShrinkagePage />} />
          <Route path="/store/alerts" element={<AlertsPage />} />
          <Route path="/settings/security" element={<SecuritySettingsPage />} />
        </Route>
      </Route>

      <Route element={<PortalProtectedRoute />}>
        <Route element={<PortalLayout />}>
          <Route path="/portal" element={<PortalDashboardPage />} />
          <Route path="/portal/documents" element={<PortalDocumentsPage />} />
          <Route path="/portal/procedures" element={<PortalProceduresPage />} />
          <Route path="/portal/legal-cases" element={<PortalLegalCasesPage />} />
          <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
